import {
  ComponentProps,
  DetailedHTMLProps,
  FC,
  InputHTMLAttributes,
  memo,
  ReactNode,
  useContext,
} from 'react'
import { useFormContext } from 'react-hook-form'
import { useEffectOnce } from 'react-use'

import { cn } from 'utils'

import baseFormFieldClasses from '../form-field.module.css'
import { RadiGroupFormFieldContext } from './RadioFormFieldGroup'
import classes from './base-radio.module.css'

type CheckboxHTMLProps = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>

type RadioTheme = 'default' | 'pill' | 'pill_primary'

export type BaseRadioFormFieldProps = {
  classNameLabel?: string
  label?: string | ReactNode
  fullWidth?: boolean
  value: string | number
  theme?: RadioTheme
} & Omit<CheckboxHTMLProps, 'value' | 'checked'>

export const _BaseRadioFormField: FC<BaseRadioFormFieldProps> = ({
  name,
  children,
  classNameLabel,
  value,
  defaultChecked,
  label,
  fullWidth,
  className,
  theme = 'default',
  ...restInputProps
}) => {
  const form = useFormContext()
  const field = useContext(RadiGroupFormFieldContext)
  const id = String(value)

  useEffectOnce(() => {
    if (name && value && defaultChecked) {
      form.setValue(name, value)
    }
  })

  return (
    <div className={cn(classes[theme], className)}>
      <input
        {...field}
        {...restInputProps}
        aria-busy={form.formState.isLoading}
        type="radio"
        value={value}
        checked={String(field?.value) === String(value)}
        className={cn('visuallyHidden', classes.input, classes[`input_${theme}`])}
        id={id}
        disabled={form.formState.isLoading || restInputProps.disabled === true}
      />
      <label
        className={cn(
          baseFormFieldClasses.label,
          classes.label,
          classes[`label_${theme}`],
          fullWidth && baseFormFieldClasses.fullWidth,
          classNameLabel,
        )}
        htmlFor={id}
      >
        {label}
      </label>
      {children}
    </div>
  )
}

export const BaseRadioFormField =
  memo<ComponentProps<typeof _BaseRadioFormField>>(_BaseRadioFormField)
